import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Video, Phone, Loader2, PhoneOff, AlertCircle } from 'lucide-react';
import { useVideoCall } from '@/hooks/use-video-call';
import { useDashboardStore } from '@/lib/store/dashboard';
import { CallingScreen } from './CallingScreen';
import { useFeatureTracking } from '@/hooks/use-feature-tracking';

interface StartVideoCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  specialistId: string;
  specialistName: string;
  caregiverId: string;
  caregiverName: string;
  onCallStarted: () => void;
  initiatedBy?: 'specialist' | 'caregiver';
}

export function StartVideoCallDialog({
  open,
  onOpenChange,
  specialistId,
  specialistName,
  caregiverId,
  caregiverName,
  onCallStarted,
  initiatedBy = 'specialist',
}: StartVideoCallDialogProps) {
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const callerId = initiatedBy === 'specialist' ? specialistId : caregiverId;
  const videoCallState = useVideoCall(callerId, initiatedBy === 'specialist');
  const { setActiveVideoCall } = useDashboardStore();

  const targetName = initiatedBy === 'specialist' ? caregiverName : specialistName;
  const targetRole = initiatedBy === 'specialist' ? 'caregiver' : 'specialist';

  // Clear error when dialog opens
  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setIsStarting(false);
    }
  }, [open]);

  // Handle when call is accepted (status changes to connected)
  useEffect(() => {
    if (videoCallState.status === 'connected' && videoCallState.token && videoCallState.wsUrl) {
      console.log('Call accepted, setting active video call');
      setActiveVideoCall({
        callId: videoCallState.callId || '',
        roomName: videoCallState.roomName || '',
        token: videoCallState.token,
        wsUrl: videoCallState.wsUrl,
        caregiverId,
        caregiverName,
        specialistId,
      });
      onCallStarted();
      onOpenChange(false);
    }
  }, [videoCallState.status, videoCallState.token, videoCallState.wsUrl, videoCallState.callId, videoCallState.roomName, setActiveVideoCall, onCallStarted, onOpenChange, caregiverId, caregiverName, specialistId]);

  // Handle call status changes (busy, declined, no_answer)
  useEffect(() => {
    if (videoCallState.status === 'busy') {
      setIsStarting(false);
      setErrorMessage(`${targetName} is currently on another call.`);
    } else if (videoCallState.status === 'no_answer') {
      setIsStarting(false);
      setErrorMessage(`${targetName} didn't answer the call.`);
    } else if (videoCallState.status === 'cancelled') {
      // This status is set when the recipient declines the call
      setIsStarting(false);
      setErrorMessage(`${targetName} declined the call.`);
    }
  }, [videoCallState.status, targetName]);

  const { trackFeatureUsage } = useFeatureTracking();

  const handleStartCall = async () => {
    setIsStarting(true);
    setErrorMessage(null);
    trackFeatureUsage('video_call_started');
    try {
      const callerName = initiatedBy === 'specialist' ? specialistName : caregiverName;
      console.log('Starting video call...', { specialistId, caregiverId, callerName, initiatedBy });
      await videoCallState.startCall(specialistId, caregiverId, callerName, initiatedBy, targetName);
    } catch (error: any) {
      console.error('Failed to start call:', error);
      setIsStarting(false);
      // Parse the error message for user-friendly display
      const message = error?.message || 'Failed to start call';
      if (message.includes('busy')) {
        setErrorMessage(`${targetName} is currently on another call. Please try again later.`);
      } else if (message.includes('not found')) {
        setErrorMessage(`Unable to reach ${targetName}. They may be offline.`);
      } else {
        setErrorMessage(`Unable to start call: ${message}`);
      }
    }
  };

  const handleCancelCall = useCallback(() => {
    videoCallState.cancelCall('cancelled');
    setIsStarting(false);
    onOpenChange(false);
  }, [videoCallState, onOpenChange]);

  const handleCallTimeout = useCallback(() => {
    videoCallState.cancelCall('timeout');
    setIsStarting(false);
    onOpenChange(false);
  }, [videoCallState, onOpenChange]);

  const handleRetry = () => {
    setErrorMessage(null);
    setIsStarting(false);
  };

  // Show calling screen when call is in progress
  if (videoCallState.status === 'calling' && videoCallState.callId) {
    return (
      <CallingScreen
        recipientName={targetName}
        recipientRole={targetRole}
        onCancel={handleCancelCall}
        onTimeout={handleCallTimeout}
        timeoutSeconds={60}
      />
    );
  }

  const showError = errorMessage || videoCallState.status === 'busy' || videoCallState.status === 'no_answer';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            Start Video Call
          </DialogTitle>
          <DialogDescription>
            Start a 1-on-1 telehealth video call with <strong>{targetName}</strong>.
            They will receive a notification to join.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center py-6">
          <div className="text-center">
            {showError ? (
              <>
                <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-3">
                  {videoCallState.status === 'busy' ? (
                    <PhoneOff className="h-8 w-8 text-destructive" />
                  ) : (
                    <AlertCircle className="h-8 w-8 text-destructive" />
                  )}
                </div>
                <p className="text-sm font-medium text-destructive mb-1">
                  {videoCallState.status === 'busy' ? 'Line Busy' : 
                   videoCallState.status === 'no_answer' ? 'No Answer' : 'Call Failed'}
                </p>
                <p className="text-sm text-muted-foreground max-w-[250px]">
                  {errorMessage || `${targetName} is unavailable. Please try again later.`}
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <Phone className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Make sure your camera and microphone are ready
                </p>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isStarting}
          >
            {showError ? 'Close' : 'Cancel'}
          </Button>
          {showError ? (
            <Button onClick={handleRetry} className="gap-2">
              Try Again
            </Button>
          ) : (
            <Button
              onClick={handleStartCall}
              disabled={isStarting}
              className="gap-2"
            >
              {isStarting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Video className="h-4 w-4" />
                  Start Call
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
